<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type PartialStudentScholarshipData = App.Students.Data.PartialStudentScholarshipData;
type StudentScholarshipData = App.Students.Data.StudentScholarshipData;

import type { Selectable } from '@/components/selectBox/selectBox';

import DataRequestSlideout from '@/domains/data-request/components/adviser/DataRequestSlideout.vue';
import DataRequestSlideoutConfirmationDialog from '@/domains/data-request/components/adviser/DataRequestSlideoutConfirmationDialog.vue';
import StudentScholarshipFormFields from '@/components/students/studentActions/StudentScholarshipFormFields.vue';

export type ShowDataRequestProps = {
  dataRequest: DataRequestData;
  scholarship: StudentScholarshipData | null;
  types: EnumData[];
  statuses: EnumData[];
};

const props = defineProps<ShowDataRequestProps>();

const [isRejectDialogOpen, rejectDialogToggle] = useToggle(false);

const dataRequestAttributes = computed(
  () => props.dataRequest.attributes as PartialStudentScholarshipData
);

const isPending = computed(() => props.dataRequest.status.value === 'pending');

const scholarshipName = computed(() => {
  if (dataRequestAttributes.value?.scholarship_name) {
    return {
      label: dataRequestAttributes.value?.scholarship_name,
      value: dataRequestAttributes.value?.scholarship_name
    };
  }
  return buildSelectableScholarship(props.scholarship?.scholarship) as Selectable<number> | null;
});

const form = useForm({
  fields: {
    type:
      ((dataRequestAttributes.value?.type ||
        props.scholarship?.type) as Selectable<string> | null) ?? null,
    institute_id:
      ((buildSelectableInstitute(dataRequestAttributes.value?.institute) ||
        buildSelectableInstitute(props.scholarship?.institute)) as Selectable<number> | null) ??
      null,
    scholarship_name: scholarshipName.value,
    amount: (dataRequestAttributes.value?.amount || props.scholarship?.amount) ?? null,
    years_renewable:
      (dataRequestAttributes.value?.years_renewable || props.scholarship?.years_renewable) ?? null,
    status:
      ((dataRequestAttributes.value?.status ||
        props.scholarship?.status) as Selectable<string> | null) ?? null
  },
  transform: (fields) => ({
    ...fields,
    data_request_id: props.dataRequest.id,
    return_to_route: 'data-requests.index',
    type: fields.type?.value,
    institute_id: fields.institute_id?.value,
    scholarship_name: fields.scholarship_name?.label,
    status: fields.status?.value
  })
});

const rejectForm = useForm({
  method: 'POST',
  url: route('data-requests.reject', {
    dataRequest: props.dataRequest.id
  }),
  fields: {
    rejected_reason: ''
  },
  hooks: {
    success() {
      rejectDialogToggle();
    }
  }
});

function handleApproveDataRequest() {
  if (props.dataRequest.type.value === 'create') {
    form.submitWith({
      method: 'POST',
      url: route('students.student-scholarships.store', {
        student: props.dataRequest.user?.id
      })
    });
  }
  if (props.dataRequest.type.value === 'edit') {
    form.submitWith({
      method: 'PUT',
      url: route('students.student-scholarships.update', {
        studentScholarship: props.dataRequest.requestable?.id,
        student: props.dataRequest.user?.id
      })
    });
  }
}

function handleRejectDataRequest() {
  rejectDialogToggle();
}

function handleRejectDataRequestConfirm() {
  rejectForm.submit();
}
</script>

<template>
  <DataRequestSlideout
    :dataRequest="dataRequest"
    :hasErrors="form.hasErrors"
    :isApproveLoading="form.processing"
    :isDirty="form.isDirty"
    @approveDataRequest="handleApproveDataRequest"
    @rejectDataRequest="handleRejectDataRequest"
  >
    <StudentScholarshipFormFields
      :form="form"
      :isReadonly="!isPending"
      :scholarship="scholarship || undefined"
      :statuses="statuses"
      :types="types"
    />
  </DataRequestSlideout>
  <DataRequestSlideoutConfirmationDialog
    :form="rejectForm"
    :isOpen="isRejectDialogOpen"
    @onConfirm="() => handleRejectDataRequestConfirm()"
    @onCancel="() => rejectDialogToggle()"
    @onClose="() => rejectDialogToggle()"
  />
</template>
